export type SortConfig = {
  columnKey: string
  sortingDirection: 'asc' | 'desc'
} | null

export type TableData = Record<string, any>

export function sortTableData<T extends TableData>(
  data: T[],
  sortConfig: SortConfig,
  customSorters?: Record<
    string,
    (a: T, b: T, direction: 'asc' | 'desc') => number
  >
): T[] {
  if (!sortConfig) return data

  const { columnKey, sortingDirection } = sortConfig
  const sortedData = [...data].sort((a, b) => {
    if (customSorters?.[columnKey]) {
      return customSorters[columnKey](a, b, sortingDirection)
    }

    let aValue = a[columnKey]
    let bValue = b[columnKey]

    if (aValue === null || aValue === undefined) aValue = ''
    if (bValue === null || bValue === undefined) bValue = ''

    if (
      isNumericColumn(columnKey) ||
      (typeof aValue === 'number' && typeof bValue === 'number')
    ) {
      const numA = Number(aValue)
      const numB = Number(bValue)
      if (!isNaN(numA) && !isNaN(numB)) {
        return sortingDirection === 'asc' ? numA - numB : numB - numA
      }
    }

    if (isDateColumn(columnKey) || isDateValue(aValue) || isDateValue(bValue)) {
      const dateA = new Date(String(aValue))
      const dateB = new Date(String(bValue))
      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        return sortingDirection === 'asc'
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime()
      }
    }

    aValue = String(aValue).toLowerCase()
    bValue = String(bValue).toLowerCase()

    if (sortingDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
    }
  })

  return sortedData
}

export function filterTableData<T extends TableData>(
  data: T[],
  filterQuery: string,
  searchableColumns?: (keyof T)[],
  customFilters?: Record<string, (value: any, query: string) => boolean>
): T[] {
  if (!filterQuery.trim()) return data

  const query = filterQuery.toLowerCase().trim()

  return data.filter(row => {
    const columnsToSearch = searchableColumns
      ? searchableColumns
      : Object.keys(row)

    return columnsToSearch.some(columnKey => {
      const value = row[columnKey]

      if (customFilters?.[String(columnKey)]) {
        return customFilters[String(columnKey)](value, query)
      }

      return String(value || '')
        .toLowerCase()
        .includes(query)
    })
  })
}

function isNumericColumn(columnKey: string): boolean {
  const numericColumns = [
    'id',
    'dealerId',
    'count',
    'amount',
    'number',
    'price',
    'quantity'
  ]
  return numericColumns.some(col => columnKey.toLowerCase().includes(col))
}

function isDateColumn(columnKey: string): boolean {
  const dateColumns = ['date', 'created', 'updated', 'expiry', 'audit']
  return dateColumns.some(col => columnKey.toLowerCase().includes(col))
}

function isDateValue(value: any): boolean {
  if (!value) return false
  const dateRegex = /^\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}|\d{2}-\d{2}-\d{4}/
  return typeof value === 'string' && dateRegex.test(value)
}
